import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, AlertTitle } from '@mui/material';

// Toast context
const ToastContext = createContext();

// Toast types
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast provider component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Add a new toast
  const addToast = useCallback((message, type = TOAST_TYPES.INFO, title = null, duration = 6000) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      title,
      duration
    };

    setToasts(prev => [...prev, newToast]);

    // Auto remove toast after duration
    setTimeout(() => {
      removeToast(id);
    }, duration);
  }, []);

  // Remove a specific toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message, title = 'Success', duration) => {
    addToast(message, TOAST_TYPES.SUCCESS, title, duration);
  }, [addToast]);

  const showError = useCallback((message, title = 'Error', duration) => {
    addToast(message, TOAST_TYPES.ERROR, title, duration);
  }, [addToast]);

  const showWarning = useCallback((message, title = 'Warning', duration) => {
    addToast(message, TOAST_TYPES.WARNING, title, duration);
  }, [addToast]);

  const showInfo = useCallback((message, title = 'Info', duration) => {
    addToast(message, TOAST_TYPES.INFO, title, duration);
  }, [addToast]);

  // Handle toast close
  const handleClose = (id) => {
    removeToast(id);
  };

  const contextValue = {
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Render toasts */}
      {toasts.map((toast) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={toast.duration}
          onClose={() => handleClose(toast.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ 
            '& .MuiSnackbar-root': {
              zIndex: 9999
            }
          }}
        >
          <Alert
            onClose={() => handleClose(toast.id)}
            severity={toast.type}
            variant="filled"
            sx={{ 
              width: '100%',
              minWidth: '300px',
              maxWidth: '500px',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            {toast.title && (
              <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
                {toast.title}
              </AlertTitle>
            )}
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
}; 